import React, { useState, useEffect } from "react";
import { db, storage } from "../firebase.config";
import { 
  getDoc, 
  doc, 
  getDocs, 
  collection, 
  updateDoc, 
  deleteDoc, 
  query, 
  where,
  addDoc,
  serverTimestamp
} from "firebase/firestore";
import { 
  ref, 
  uploadBytesResumable, 
  getDownloadURL, 
  deleteObject 
} from "firebase/storage";
import { useNavigate, useParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useAuth } from "../contexts/AuthContext";
import {
  Home,
  Building2,
  Warehouse,
  Church,
  Mountain,
  Briefcase,
  Star,
  Pencil,
  Trash2,
  Plus,
  Phone,
  Mail,
  MapPin,
  BadgeCheck,
  Store,
  TrendingUp,
  Award,
  X,
  Image,
  Upload,
  DollarSign,
  Users,
  Calendar,
  Settings,
  Eye
} from "lucide-react";

const AgentProfile = () => {
  const { agentId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  // State variables
  const [agent, setAgent] = useState(null);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [updateAgentData, setUpdateAgentData] = useState({});
  const [updatePropertyData, setUpdatePropertyData] = useState({
    title: "",
    propertyType: "",
    address: "",
    price: "",
    rentPrice: "",
    area: "",
    bedrooms: "",
    bathrooms: "",
    city: "",
    location: "",
    yearBuilt: "",
    description: "",
    propertyImages: [],
    type: "sale"
  });
  const [agentUpdateFormVisible, setAgentUpdateFormVisible] = useState(false);
  const [updateModalVisible, setUpdateModalVisible] = useState(false);
  const [currentPropertyId, setCurrentPropertyId] = useState(null);
  const [selectedImages, setSelectedImages] = useState([]);
  const [progress, setProgress] = useState(0);
  const [isOwner, setIsOwner] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);

  // Property type icons mapping
  const propertyTypeIcons = {
    house: <Home className="h-5 w-5" />,
    apartment: <Building2 className="h-5 w-5" />,
    villa: <Warehouse className="h-5 w-5" />,
    commercial: <Briefcase className="h-5 w-5" />,
    land: <Mountain className="h-5 w-5" />,
    office: <Church className="h-5 w-5" />
  };

  // Fetch agent profile and properties
  useEffect(() => {
    const fetchAgentData = async () => {
      setLoading(true);
      try {
        // Fetch agent data
        const agentDocRef = doc(db, "Featured-Agents", agentId);
        const agentDocSnap = await getDoc(agentDocRef);

        if (agentDocSnap.exists()) {
          const agentData = { id: agentDocSnap.id, ...agentDocSnap.data() };
          setAgent(agentData);
          setUpdateAgentData(agentData);
          
          // Check if current user is the owner of this agency
          if (currentUser && agentData.userId === currentUser.uid) {
            setIsOwner(true);
          }
        } else {
          setError("No such agent found!");
        }

        // Fetch agent's properties
        const propertiesRef = collection(db, "properties");
        const q = query(propertiesRef, where("agentId", "==", agentId));
        const propertiesSnapshot = await getDocs(q);
        const propertiesList = propertiesSnapshot.docs
          .map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }))
          .sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));

        setProperties(propertiesList);
      } catch (error) {
        console.error("Error fetching data:", error);
        setError(`Error fetching agent profile or properties: ${error.message}`);
      } finally {
        setLoading(false);
      }
    };

    if (agentId) {
      fetchAgentData();
    }
  }, [agentId, currentUser]);

  // Function to remove property image
  const handleRemoveImage = async (index) => {
    if (!updatePropertyData.propertyImages || updatePropertyData.propertyImages.length === 0) return;
    
    const imageUrl = updatePropertyData.propertyImages[index];
    if (!imageUrl) return;

    try {
      // Extract path from URL
      const imageRef = ref(storage, imageUrl);
      await deleteObject(imageRef);
      
      const updatedImages = updatePropertyData.propertyImages.filter((_, i) => i !== index);
      const propertyDocRef = doc(db, "properties", currentPropertyId);

      await updateDoc(propertyDocRef, {
        propertyImages: updatedImages,
      });

      setUpdatePropertyData((prevData) => ({
        ...prevData,
        propertyImages: updatedImages,
      }));

      toast.success("Image deleted successfully!");
    } catch (error) {
      console.error("Error deleting image:", error);
      toast.error(`Error deleting image: ${error.message}`);
    }
  };

  // Function to update agent profile
  const handleAgentProfileUpdate = async () => {
    if (!updateAgentData.sellerName || !updateAgentData.phoneNumber) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      const agentDocRef = doc(db, "Featured-Agents", agentId);
      await updateDoc(agentDocRef, {
        sellerName: updateAgentData.sellerName,
        phoneNumber: updateAgentData.phoneNumber,
        city: updateAgentData.city,
        location: updateAgentData.location,
        address: updateAgentData.address,
        ranking: Number(updateAgentData.ranking),
        updatedAt: serverTimestamp()
      });
      
      setAgent(prev => ({ ...prev, ...updateAgentData }));
      setAgentUpdateFormVisible(false);
      toast.success("Agency profile updated successfully!");
    } catch (error) {
      console.error("Error updating agent:", error);
      toast.error(`Error updating agency profile: ${error.message}`);
    }
  };

  // Function to upload thumbnail
  const handleThumbnailUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size should be less than 5MB");
      return;
    }

    // Validate file type
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }

    const storageRef = ref(storage, `agency-thumbnails/${agentId}/${Date.now()}-${file.name}`);
    const uploadTask = uploadBytesResumable(storageRef, file);

    uploadTask.on(
      "state_changed",
      (snapshot) => {
        const progressPercent = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
        setProgress(progressPercent);
      },
      (error) => {
        console.error("Upload error:", error);
        toast.error(`Error uploading thumbnail: ${error.message}`);
      },
      async () => {
        try {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
          const agentDocRef = doc(db, "Featured-Agents", agentId);
          await updateDoc(agentDocRef, { thumbnail: downloadURL });
          setUpdateAgentData(prev => ({ ...prev, thumbnail: downloadURL }));
          setAgent(prev => ({ ...prev, thumbnail: downloadURL }));
          toast.success("Profile image uploaded successfully!");
          setProgress(0);
        } catch (error) {
          console.error("Error saving URL:", error);
          toast.error(`Error saving profile image URL: ${error.message}`);
        }
      }
    );
  };

  // Function to set primary image
  const handleImageClick = async (imageURL) => {
    const updatedImages = [imageURL, ...updatePropertyData.propertyImages.filter(img => img !== imageURL)];

    try {
      const propertyDocRef = doc(db, "properties", currentPropertyId);
      await updateDoc(propertyDocRef, {
        propertyImages: updatedImages,
      });

      setUpdatePropertyData((prevData) => ({
        ...prevData,
        propertyImages: updatedImages,
      }));

      toast.success("Primary image updated!");
    } catch (error) {
      console.error("Error updating primary image:", error);
      toast.error(`Error updating primary image: ${error.message}`);
    }
  };

  // Function to upload property images
  const handleImageSelection = async (e) => {
    const files = Array.from(e.target.files);
    const currentImageCount = updatePropertyData.propertyImages?.length || 0;
    
    if (files.length + currentImageCount > 10) {
      toast.error("You can upload up to 10 images only.");
      return;
    }

    setUploadingImages(true);
    const uploadPromises = files.map((file) => {
      // Validate file
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name} is larger than 5MB`);
        return null;
      }
      if (!file.type.startsWith("image/")) {
        toast.error(`${file.name} is not an image file`);
        return null;
      }

      const storageRef = ref(storage, `properties/${agentId}/${currentPropertyId}/${Date.now()}-${file.name}`);
      const uploadTask = uploadBytesResumable(storageRef, file);

      return new Promise((resolve, reject) => {
        uploadTask.on(
          "state_changed",
          null,
          (error) => reject(error),
          async () => {
            try {
              const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
              resolve(downloadURL);
            } catch (error) {
              reject(error);
            }
          }
        );
      });
    });

    try {
      const urls = (await Promise.all(uploadPromises)).filter(url => url !== null);
      const updatedImages = [...(updatePropertyData.propertyImages || []), ...urls];
      
      // Update Firestore
      const propertyDocRef = doc(db, "properties", currentPropertyId);
      await updateDoc(propertyDocRef, {
        propertyImages: updatedImages,
      });

      setUpdatePropertyData((prevData) => ({
        ...prevData,
        propertyImages: updatedImages,
      }));
      
      toast.success(`${urls.length} image(s) uploaded successfully!`);
    } catch (error) {
      console.error("Error uploading images:", error);
      toast.error(`Error uploading images: ${error.message}`);
    } finally {
      setUploadingImages(false);
      setSelectedImages([]);
    }
  };

  // Function to delete property
  const handleDeleteProperty = async (propertyId) => {
    if (window.confirm("Are you sure you want to delete this property? This action cannot be undone.")) {
      try {
        // First, delete all images from storage
        const propertyToDelete = properties.find(p => p.id === propertyId);
        if (propertyToDelete?.propertyImages?.length > 0) {
          const deletePromises = propertyToDelete.propertyImages.map(async (imageUrl) => {
            try {
              const imageRef = ref(storage, imageUrl);
              await deleteObject(imageRef);
            } catch (error) {
              console.error("Error deleting image:", error);
            }
          });
          await Promise.all(deletePromises);
        }

        // Delete property document from Firestore
        const propertyDocRef = doc(db, "properties", propertyId);
        await deleteDoc(propertyDocRef);
        
        setProperties((prev) => prev.filter((property) => property.id !== propertyId));
        toast.success("Property deleted successfully!");
      } catch (error) {
        console.error("Error deleting property:", error);
        toast.error(`Failed to delete property: ${error.message}`);
      }
    }
  };

  // Function to update property
  const handleUpdateProperty = async () => {
    if (!updatePropertyData.title || !updatePropertyData.propertyType) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      const propertyDocRef = doc(db, "properties", currentPropertyId);
      
      const updatedData = {
        title: updatePropertyData.title,
        propertyType: updatePropertyData.propertyType,
        address: updatePropertyData.address,
        price: updatePropertyData.type === 'sale' ? parseFloat(updatePropertyData.price) || 0 : 0,
        rentPrice: updatePropertyData.type === 'rent' ? parseFloat(updatePropertyData.rentPrice) || 0 : 0,
        area: parseFloat(updatePropertyData.area) || 0,
        bedrooms: parseInt(updatePropertyData.bedrooms) || 0,
        bathrooms: parseInt(updatePropertyData.bathrooms) || 0,
        city: updatePropertyData.city,
        location: updatePropertyData.location,
        yearBuilt: parseInt(updatePropertyData.yearBuilt) || new Date().getFullYear(),
        description: updatePropertyData.description,
        type: updatePropertyData.type,
        updatedAt: serverTimestamp()
      };

      await updateDoc(propertyDocRef, updatedData);
      toast.success("Property updated successfully!");
      setUpdateModalVisible(false);
      
      // Refresh properties list
      const propertiesRef = collection(db, "properties");
      const q = query(propertiesRef, where("agentId", "==", agentId));
      const propertiesSnapshot = await getDocs(q);
      const propertiesList = propertiesSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setProperties(propertiesList);
    } catch (error) {
      console.error("Error updating property:", error);
      toast.error(`Error updating property: ${error.message}`);
    }
  };

  // Function to redirect to add property form - Updated route
  const handleRedirectToAddProperty = () => {
    navigate(`/add-property-to-agent/${agentId}`);
  };

  // Function to render star rating
  const renderRating = (rating) => {
    return (
      <div className="flex items-center">
        {[...Array(5)].map((_, i) => (
          <Star
            key={i}
            className={`h-5 w-5 ${
              i < Math.floor(rating) ? "text-yellow-400 fill-current" : "text-gray-300"
            }`}
          />
        ))}
        <span className="ml-2 text-sm text-gray-600 font-medium">({rating})</span>
      </div>
    );
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-emerald-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 font-medium">Loading agency profile...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center bg-white rounded-xl p-8 shadow-lg max-w-md">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <p className="text-red-500 text-lg">{error}</p>
          <button
            onClick={() => navigate("/")}
            className="mt-6 bg-emerald-600 text-white px-6 py-2 rounded-lg hover:bg-emerald-700 transition"
          >
            Go Back Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 pb-12">
      <ToastContainer position="top-right" autoClose={3000} />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
        {/* Agency Profile Header */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden mb-8">
          {/* Cover Image Area */}
          <div className="h-48 bg-gradient-to-r from-emerald-600 to-green-600 relative">
            {isOwner && (
              <button
                onClick={() => setAgentUpdateFormVisible(true)}
                className="absolute top-4 right-4 bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white p-2 rounded-full transition"
              >
                <Pencil className="h-5 w-5" />
              </button>
            )}
          </div>
          
          <div className="px-8 pb-8 relative">
            {/* Profile Image */}
            <div className="relative -mt-20 mb-6">
              <div className="relative inline-block">
                <img
                  src={agent?.thumbnail || "https://via.placeholder.com/150x150?text=Agency"}
                  className="w-32 h-32 rounded-full shadow-xl border-4 border-white object-cover bg-white"
                  alt="Agency Logo"
                />
                {agent?.ranking >= 4 && (
                  <div className="absolute -top-2 -right-2 bg-yellow-400 text-white p-1.5 rounded-full shadow-lg">
                    <BadgeCheck className="h-5 w-5" />
                  </div>
                )}
              </div>
            </div>

            {/* Agency Info */}
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
              <div className="flex-1">
                <div className="flex items-center gap-3 flex-wrap mb-3">
                  <h1 className="text-3xl font-bold text-gray-900">{agent?.sellerName}</h1>
                  {agent?.ranking && renderRating(agent.ranking)}
                  {agent?.status === "active" && (
                    <span className="bg-green-100 text-green-700 text-xs font-semibold px-2 py-1 rounded-full">
                      Active
                    </span>
                  )}
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div className="flex items-center text-gray-700">
                    <Phone className="h-5 w-5 text-emerald-600 mr-3" />
                    <span>{agent?.phoneNumber}</span>
                  </div>
                  <div className="flex items-center text-gray-700">
                    <Mail className="h-5 w-5 text-emerald-600 mr-3" />
                    <span>{agent?.userEmail}</span>
                  </div>
                  <div className="flex items-center text-gray-700">
                    <MapPin className="h-5 w-5 text-emerald-600 mr-3" />
                    <span>{agent?.city}, {agent?.location}</span>
                  </div>
                  <div className="flex items-center text-gray-700">
                    <Store className="h-5 w-5 text-emerald-600 mr-3" />
                    <span className="truncate">{agent?.address}</span>
                  </div>
                </div>
              </div>

              {/* Stats Cards */}
              <div className="flex gap-4">
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 text-center min-w-[100px]">
                  <div className="text-blue-600 text-2xl font-bold">{properties.length}</div>
                  <div className="text-gray-600 text-sm">Properties</div>
                </div>
                <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4 text-center min-w-[100px]">
                  <div className="text-purple-600 text-2xl font-bold">{agent?.ranking || "N/A"}</div>
                  <div className="text-gray-600 text-sm">Rating</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Add Property Button - Only visible to owner */}
        {isOwner && (
          <div className="flex justify-center mb-8">
            <button
              onClick={handleRedirectToAddProperty}
              className="bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700 text-white font-semibold px-8 py-3 rounded-xl shadow-lg transition duration-300 transform hover:scale-105 flex items-center gap-2"
            >
              <Plus className="h-5 w-5" />
              Add New Property
            </button>
          </div>
        )}

        {/* Properties Section */}
        <div>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Properties by {agent?.sellerName}</h2>
            <div className="text-gray-600 bg-white px-4 py-2 rounded-lg shadow-sm">
              Total: {properties.length} properties
            </div>
          </div>
          
          {properties.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-xl shadow-md">
              <Home className="mx-auto h-16 w-16 text-gray-400" />
              <h3 className="mt-4 text-lg font-medium text-gray-900">No properties listed</h3>
              <p className="mt-2 text-gray-600">This agency hasn't listed any properties yet.</p>
              {isOwner && (
                <button
                  onClick={handleRedirectToAddProperty}
                  className="mt-6 bg-emerald-600 text-white px-6 py-2 rounded-lg hover:bg-emerald-700 transition"
                >
                  List Your First Property
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {properties.map((property) => (
                <div key={property.id} className="bg-white rounded-xl shadow-md hover:shadow-xl transition-shadow overflow-hidden group">
                  {/* Property Image */}
                  <div className="h-56 bg-gray-200 overflow-hidden relative">
                    <img
                      src={property.propertyImages?.[0] || "https://via.placeholder.com/300x200?text=No+Image"}
                      alt={property.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                    <div className="absolute top-3 right-3 bg-gradient-to-r from-emerald-600 to-green-600 text-white px-3 py-1 rounded-full text-sm font-semibold shadow-md">
                      For {property.type === 'sale' ? 'Sale' : 'Rent'}
                    </div>
                    <div className="absolute bottom-3 left-3 bg-white bg-opacity-90 backdrop-blur-sm px-2 py-1 rounded-lg text-sm flex items-center gap-1 shadow-md">
                      {propertyTypeIcons[property.propertyType] || <Home className="h-4 w-4" />}
                      <span className="capitalize font-medium">{property.propertyType}</span>
                    </div>
                  </div>

                  {/* Property Details */}
                  <div className="p-5">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-1">{property.title}</h3>
                    <p className="text-gray-600 text-sm mb-3 line-clamp-2">{property.description}</p>
                    
                    <div className="grid grid-cols-2 gap-3 text-sm text-gray-700 mb-4">
                      <div className="flex items-center">
                        <DollarSign className="h-4 w-4 text-emerald-600 mr-1" />
                        <span className="font-medium text-emerald-600">
                          {property.type === 'rent' ? `${property.rentPrice}/month` : `$${property.price}`}
                        </span>
                      </div>
                      <div className="flex items-center">
                        <Building2 className="h-4 w-4 text-gray-500 mr-1" />
                        <span>{property.area} sq ft</span>
                      </div>
                      <div className="flex items-center">
                        <Users className="h-4 w-4 text-gray-500 mr-1" />
                        <span>{property.bedrooms} bd / {property.bathrooms} ba</span>
                      </div>
                      <div className="flex items-center">
                        <MapPin className="h-4 w-4 text-gray-500 mr-1" />
                        <span className="truncate">{property.city}</span>
                      </div>
                    </div>

                    {isOwner && (
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            setCurrentPropertyId(property.id);
                            setUpdatePropertyData({
                              title: property.title || "",
                              propertyType: property.propertyType || "",
                              address: property.address || "",
                              price: property.price || "",
                              rentPrice: property.rentPrice || "",
                              area: property.area || "",
                              bedrooms: property.bedrooms || "",
                              bathrooms: property.bathrooms || "",
                              city: property.city || "",
                              location: property.location || "",
                              yearBuilt: property.yearBuilt || "",
                              description: property.description || "",
                              propertyImages: property.propertyImages || [],
                              type: property.type || "sale"
                            });
                            setUpdateModalVisible(true);
                          }}
                          className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-2 rounded-lg text-sm transition-colors flex items-center justify-center gap-1"
                        >
                          <Pencil className="h-4 w-4" />
                          Update
                        </button>
                        <button
                          onClick={() => handleDeleteProperty(property.id)}
                          className="bg-red-500 hover:bg-red-600 text-white px-3 py-2 rounded-lg text-sm transition-colors"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Update Agency Modal */}
      {agentUpdateFormVisible && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto mx-4">
            <div className="sticky top-0 bg-gradient-to-r from-emerald-600 to-green-600 px-6 py-4 flex justify-between items-center">
              <h3 className="text-2xl font-bold text-white">Update Agency Profile</h3>
              <button
                onClick={() => setAgentUpdateFormVisible(false)}
                className="text-white hover:text-gray-200 transition"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">Agency Name *</label>
                  <input
                    type="text"
                    value={updateAgentData.sellerName || ''}
                    onChange={(e) => setUpdateAgentData(prev => ({ ...prev, sellerName: e.target.value }))}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">Phone Number *</label>
                  <input
                    type="tel"
                    value={updateAgentData.phoneNumber || ''}
                    onChange={(e) => setUpdateAgentData(prev => ({ ...prev, phoneNumber: e.target.value }))}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">City *</label>
                  <input
                    type="text"
                    value={updateAgentData.city || ''}
                    onChange={(e) => setUpdateAgentData(prev => ({ ...prev, city: e.target.value }))}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">Location/Area *</label>
                  <input
                    type="text"
                    value={updateAgentData.location || ''}
                    onChange={(e) => setUpdateAgentData(prev => ({ ...prev, location: e.target.value }))}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">Street Address *</label>
                  <textarea
                    value={updateAgentData.address || ''}
                    onChange={(e) => setUpdateAgentData(prev => ({ ...prev, address: e.target.value }))}
                    rows="3"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">Agency Rating (1-5) *</label>
                  <div className="flex items-center gap-2 mb-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => setUpdateAgentData(prev => ({ ...prev, ranking: star }))}
                        className={`text-3xl transition-transform hover:scale-110 ${
                          updateAgentData.ranking >= star ? 'text-yellow-400' : 'text-gray-300'
                        }`}
                      >
                        ★
                      </button>
                    ))}
                  </div>
                  <select
                    value={updateAgentData.ranking || ''}
                    onChange={(e) => setUpdateAgentData(prev => ({ ...prev, ranking: parseInt(e.target.value) }))}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                  >
                    <option value="">Select rating</option>
                    <option value="1">⭐ 1 Star - Beginner</option>
                    <option value="2">⭐⭐ 2 Stars - Intermediate</option>
                    <option value="3">⭐⭐⭐ 3 Stars - Professional</option>
                    <option value="4">⭐⭐⭐⭐ 4 Stars - Advanced</option>
                    <option value="5">⭐⭐⭐⭐⭐ 5 Stars - Expert</option>
                  </select>
                </div>
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">Profile Image</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleThumbnailUpload}
                    className="w-full p-2 border border-gray-300 rounded-lg"
                  />
                  {progress > 0 && progress < 100 && (
                    <div className="mt-2">
                      <div className="bg-gray-200 rounded-full h-2 overflow-hidden">
                        <div className="bg-emerald-500 h-2 rounded-full transition-all" style={{ width: `${progress}%` }} />
                      </div>
                      <p className="text-sm text-gray-600 mt-1">Uploading: {progress}%</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-8">
                <button
                  onClick={handleAgentProfileUpdate}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-2 rounded-lg transition-colors"
                >
                  Save Changes
                </button>
                <button
                  onClick={() => setAgentUpdateFormVisible(false)}
                  className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-2 rounded-lg transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Update Property Modal */}
      {updateModalVisible && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto mx-4">
            <div className="sticky top-0 bg-gradient-to-r from-emerald-600 to-green-600 px-6 py-4 flex justify-between items-center">
              <h3 className="text-2xl font-bold text-white">Update Property</h3>
              <button
                onClick={() => setUpdateModalVisible(false)}
                className="text-white hover:text-gray-200 transition"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <input
                  type="text"
                  placeholder="Property Title *"
                  value={updatePropertyData.title || ''}
                  onChange={(e) => setUpdatePropertyData(prev => ({ ...prev, title: e.target.value }))}
                  className="p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                />
                <select
                  value={updatePropertyData.propertyType || ''}
                  onChange={(e) => setUpdatePropertyData(prev => ({ ...prev, propertyType: e.target.value }))}
                  className="p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="">Select Property Type *</option>
                  <option value="house">House</option>
                  <option value="apartment">Apartment</option>
                  <option value="villa">Villa</option>
                  <option value="commercial">Commercial</option>
                  <option value="land">Land</option>
                  <option value="office">Office</option>
                </select>
                <select
                  value={updatePropertyData.type || 'sale'}
                  onChange={(e) => setUpdatePropertyData(prev => ({ ...prev, type: e.target.value }))}
                  className="p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="sale">For Sale</option>
                  <option value="rent">For Rent</option>
                </select>
                {updatePropertyData.type === 'sale' ? (
                  <input
                    type="number"
                    placeholder="Sale Price ($)"
                    value={updatePropertyData.price || ''}
                    onChange={(e) => setUpdatePropertyData(prev => ({ ...prev, price: e.target.value }))}
                    className="p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                  />
                ) : (
                  <input
                    type="number"
                    placeholder="Rent Price ($/month)"
                    value={updatePropertyData.rentPrice || ''}
                    onChange={(e) => setUpdatePropertyData(prev => ({ ...prev, rentPrice: e.target.value }))}
                    className="p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                  />
                )}
                <input
                  type="number"
                  placeholder="Area (sq ft) *"
                  value={updatePropertyData.area || ''}
                  onChange={(e) => setUpdatePropertyData(prev => ({ ...prev, area: e.target.value }))}
                  className="p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                />
                <input
                  type="number"
                  placeholder="Bedrooms"
                  value={updatePropertyData.bedrooms || ''}
                  onChange={(e) => setUpdatePropertyData(prev => ({ ...prev, bedrooms: e.target.value }))}
                  className="p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                />
                <input
                  type="number"
                  placeholder="Bathrooms"
                  value={updatePropertyData.bathrooms || ''}
                  onChange={(e) => setUpdatePropertyData(prev => ({ ...prev, bathrooms: e.target.value }))}
                  className="p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                />
                <input
                  type="text"
                  placeholder="City *"
                  value={updatePropertyData.city || ''}
                  onChange={(e) => setUpdatePropertyData(prev => ({ ...prev, city: e.target.value }))}
                  className="p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                />
                <input
                  type="text"
                  placeholder="Location/Area *"
                  value={updatePropertyData.location || ''}
                  onChange={(e) => setUpdatePropertyData(prev => ({ ...prev, location: e.target.value }))}
                  className="p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                />
                <input
                  type="text"
                  placeholder="Address *"
                  value={updatePropertyData.address || ''}
                  onChange={(e) => setUpdatePropertyData(prev => ({ ...prev, address: e.target.value }))}
                  className="p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              <textarea
                placeholder="Property Description"
                value={updatePropertyData.description || ''}
                onChange={(e) => setUpdatePropertyData(prev => ({ ...prev, description: e.target.value }))}
                rows="3"
                className="w-full p-3 border border-gray-300 rounded-lg mb-4 focus:ring-2 focus:ring-emerald-500"
              />

              <div className="mb-4">
                <label className="block text-gray-700 font-semibold mb-2">Property Images:</label>
                <div className="flex items-center gap-2 mb-2">
                  <label className="cursor-pointer bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg transition flex items-center gap-2">
                    <Upload className="h-4 w-4" />
                    Upload Images
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={handleImageSelection}
                      className="hidden"
                    />
                  </label>
                  {uploadingImages && (
                    <div className="flex items-center gap-2">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-emerald-600"></div>
                      <span className="text-sm text-gray-600">Uploading...</span>
                    </div>
                  )}
                </div>
                <p className="text-sm text-gray-600">Click on an image to set as primary (max 10 images)</p>
              </div>

              <div className="flex flex-wrap gap-3 mb-4">
                {updatePropertyData.propertyImages?.map((imageURL, index) => (
                  <div key={index} className="relative group">
                    <img
                      src={imageURL}
                      alt={`Property ${index + 1}`}
                      className="w-24 h-24 object-cover rounded-lg cursor-pointer border-2 border-gray-300 hover:border-emerald-500 transition"
                      onClick={() => handleImageClick(imageURL)}
                    />
                    <button
                      onClick={() => handleRemoveImage(index)}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600 transition"
                    >
                      ×
                    </button>
                    {index === 0 && (
                      <span className="absolute bottom-1 left-1 bg-emerald-600 text-white text-xs px-1 rounded">Primary</span>
                    )}
                  </div>
                ))}
              </div>

              <div className="flex justify-end gap-3">
                <button
                  onClick={handleUpdateProperty}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-2 rounded-lg transition-colors"
                >
                  Update Property
                </button>
                <button
                  onClick={() => setUpdateModalVisible(false)}
                  className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-2 rounded-lg transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgentProfile;